/**
 * MultiSelectFilter — shared searchable, checkbox-driven multi-select
 * with Include/Exclude toggle.  Originally lived inline in BillingTab.jsx;
 * extracted here (May 2026) so the Eligibility tab can use the same
 * filter shape for its client/provider pickers and get free parity with
 * Billing (tested UX, identical keyboard/selection behavior).
 *
 * Props:
 *   items        — array of { id, label, sublabel? } records to render
 *   selected     — array of selected ids (also tolerates legacy 'all'
 *                  / single-id strings and normalises to []).
 *   onChange     — (nextIds) => void
 *   mode         — 'include' | 'exclude'
 *   onModeChange — (nextMode) => void (pass undefined to hide the toggle)
 *   shortcutLabel / shortcutIds — DEPRECATED single quick-select pill.
 *                  Prefer `shortcuts` for multi-shortcut UIs.
 *   shortcuts    — optional array of { label, ids, testId? } shortcut
 *                  buttons rendered as a row above the search box.
 *                  Each click toggles its full id-set on/off
 *                  exclusively (replaces current selection).  Used
 *                  e.g. for "My Clients" + "My Supervisees" on the
 *                  Provider filter.
 *   emptyLabel   — label shown when nothing is selected (default "All …")
 *   entity       — singular noun used in placeholders / labels ("provider", "client")
 *   triggerTestId — data-testid for the trigger button
 *   popoverWidth — px width of the popover (default 300)
 *   clearable    — show an inline X clear button on the trigger when
 *                  something is selected (default true).  One-click
 *                  reset without opening the popover.
 */
import React from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { ChevronsUpDown, X } from 'lucide-react';

// Normalise whatever was passed in (array, 'all' sentinel, single id,
// null/undefined) into a clean array.  Belt-and-braces against the
// legacy 'all' state that some consumers still hand us — see
// BillingTab.jsx line ~587 (May 2026 audit).
const _toArr = (value) => {
  if (Array.isArray(value)) return value;
  if (!value || value === 'all') return [];
  return [value];
};

export const MultiSelectFilter = ({
  items,
  selected,
  onChange,
  mode = 'include',
  onModeChange,
  shortcutLabel,
  shortcutIds = [],
  shortcuts,
  emptyLabel,
  entity = 'item',
  triggerTestId,
  popoverWidth = 300,
  clearable = true,
  className = '',
  footer = null,
}) => {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');

  // Persistent {id → label} cache.  The trigger looks up labels from
  // the live `items` array, but if the parent component pages the
  // items in (e.g. after a tab switch the clients list briefly empties
  // before reloading), the lookup fails and the trigger falls back to
  // "1 client" instead of the actual name.  We seed this cache every
  // time we see a fresh items array AND every time selection changes
  // — so the most-recently-seen label for a given id survives an empty
  // items render.
  const labelCacheRef = React.useRef({});
  React.useEffect(() => {
    if (!Array.isArray(items)) return;
    items.forEach((it) => {
      if (it && it.id != null) labelCacheRef.current[it.id] = it.label;
    });
  }, [items]);

  const selectedArr = React.useMemo(() => _toArr(selected), [selected]);
  const isExclude = mode === 'exclude';
  // English plural — handle "status" → "statuses" and other -s endings
  // so the placeholder text reads naturally.  Falls back to +"s" for
  // most other singular nouns ("client" → "clients").
  const plural = entity.endsWith('s') ? `${entity}es` : `${entity}s`;

  const filteredItems = React.useMemo(() => {
    if (!search) return items;
    const q = search.toLowerCase();
    return (items || []).filter(it => `${it.label} ${it.sublabel || ''}`.toLowerCase().includes(q));
  }, [items, search]);

  const toggle = (id) => {
    const next = selectedArr.includes(id)
      ? selectedArr.filter(x => x !== id)
      : [...selectedArr, id];
    onChange(next);
  };

  // (Legacy `allShortcutSelected` was inlined into the shortcut row
  // builder below — multi-shortcut support computes per-button.)

  // Resolve the human label for a selected id, with a cached fallback
  // so the trigger stays informative even when `items` is briefly empty.
  const labelFor = (id) => {
    const fromItems = (items || []).find(i => i.id === id)?.label;
    return fromItems || labelCacheRef.current[id] || null;
  };

  // Trigger-label shortcut detection — checks every configured shortcut
  // (legacy `shortcutLabel`/`shortcutIds` OR the new `shortcuts` array)
  // so when a shortcut is fully selected the trigger reads "My Clients"
  // instead of "1 provider" / "5 providers".
  const _allShortcuts = Array.isArray(shortcuts) && shortcuts.length > 0
    ? shortcuts.filter(s => s && Array.isArray(s.ids) && s.ids.length > 0 && s.label)
    : (shortcutIds.length > 0 && shortcutLabel ? [{ label: shortcutLabel, ids: shortcutIds }] : []);
  const _activeShortcut = _allShortcuts.find(s =>
    s.ids.length === selectedArr.length && s.ids.every(id => selectedArr.includes(id))
  );

  const labelText = _activeShortcut
    ? (isExclude ? `Excluding ${_activeShortcut.label}` : _activeShortcut.label)
    : selectedArr.length === 0
      ? (emptyLabel || `All ${plural.charAt(0).toUpperCase() + plural.slice(1)}`)
      : selectedArr.length === 1
        ? `${isExclude ? 'Excluding: ' : ''}${labelFor(selectedArr[0]) || `1 ${entity}`}`
        : `${isExclude ? 'Excluding ' : ''}${selectedArr.length} ${plural}`;

  const showInlineClear = clearable && selectedArr.length > 0;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={`${className || 'w-full'} justify-between font-normal h-10 text-sm ${isExclude && selectedArr.length > 0 ? 'border-orange-400 text-orange-900' : ''}`}
          data-testid={triggerTestId}
        >
          <span className="truncate flex-1 text-left">{labelText}</span>
          {showInlineClear && (
            <span
              role="button"
              tabIndex={0}
              aria-label={`Clear ${plural} filter`}
              onClick={(e) => {
                // Click the X without opening the popover.
                e.stopPropagation();
                e.preventDefault();
                onChange([]);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.stopPropagation();
                  e.preventDefault();
                  onChange([]);
                }
              }}
              className="ml-1 inline-flex items-center justify-center h-5 w-5 rounded hover:bg-red-100 cursor-pointer shrink-0"
              data-testid={triggerTestId ? `${triggerTestId}-inline-clear` : undefined}
              title="Clear selection"
            >
              <X className="h-3.5 w-3.5 opacity-70" />
            </span>
          )}
          <ChevronsUpDown className="ml-1 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0" style={{ width: popoverWidth }} align="start">
        {typeof onModeChange === 'function' && (
          <div className="p-2 border-b bg-gray-50 flex items-center gap-1" role="tablist" aria-label="Filter mode">
            <Button
              type="button"
              variant={!isExclude ? 'default' : 'outline'}
              size="sm"
              className="flex-1 h-7 text-xs"
              onClick={() => onModeChange('include')}
              data-testid={`${triggerTestId}-mode-include`}
              title={`Show only the checked ${plural}`}
            >
              Include
            </Button>
            <Button
              type="button"
              variant={isExclude ? 'default' : 'outline'}
              size="sm"
              className={`flex-1 h-7 text-xs ${isExclude ? 'bg-orange-600 hover:bg-orange-700' : ''}`}
              onClick={() => onModeChange('exclude')}
              data-testid={`${triggerTestId}-mode-exclude`}
              title={`Hide the checked ${plural} (show everyone else)`}
            >
              Exclude
            </Button>
          </div>
        )}
        {/* Shortcut row — supports legacy single shortcut OR a list of
            shortcuts via the `shortcuts` prop.  Each button toggles its
            full id-set exclusively (clicking it again clears). */}
        {(() => {
          const list = Array.isArray(shortcuts) && shortcuts.length > 0
            ? shortcuts.filter(s => s && Array.isArray(s.ids) && s.ids.length > 0 && s.label)
            : (shortcutIds.length > 0 && shortcutLabel ? [{ label: shortcutLabel, ids: shortcutIds, testId: `${triggerTestId}-shortcut` }] : []);
          if (list.length === 0) return null;
          return (
            <div className="p-2 border-b bg-teal-50 flex flex-wrap gap-2">
              {list.map((s, idx) => {
                const active = s.ids.length > 0
                  && s.ids.every(id => selectedArr.includes(id))
                  && selectedArr.length === s.ids.length;
                return (
                  <Button
                    key={s.testId || `${s.label}-${idx}`}
                    variant={active ? 'default' : 'outline'}
                    size="sm"
                    className="flex-1 min-w-[40%] h-8 text-xs"
                    onClick={() => onChange(active ? [] : s.ids)}
                    data-testid={s.testId || `${triggerTestId}-shortcut-${idx}`}
                  >
                    {active ? `✓ ${s.label}` : `Show ${s.label}`}
                  </Button>
                );
              })}
            </div>
          );
        })()}
        <div className="p-2 border-b">
          <Input
            placeholder={`Search ${plural}…`}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-8 text-sm"
            data-testid={`${triggerTestId}-search`}
          />
        </div>
        <div className="max-h-[260px] overflow-y-auto p-1">
          {(filteredItems || []).length === 0 ? (
            <p className="text-xs text-gray-500 p-2 text-center">No {plural} found</p>
          ) : (
            (filteredItems || []).map(it => {
              const isChecked = selectedArr.includes(it.id);
              return (
                <label
                  key={it.id}
                  className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer hover:bg-gray-100 text-sm ${isChecked && isExclude ? 'bg-orange-50' : ''}`}
                  data-testid={`${triggerTestId}-option-${it.id}`}
                >
                  <Checkbox
                    checked={isChecked}
                    onCheckedChange={() => toggle(it.id)}
                    className={`h-4 w-4 shrink-0 ${isChecked && isExclude ? 'border-orange-500 data-[state=checked]:bg-orange-600' : ''}`}
                  />
                  {/* Optional dot — used by the Labels filter to keep
                      the color-coded affordance the standalone popover
                      had before standardization.  Items pass `color`
                      to opt in; default renders no dot. */}
                  {it.color && (
                    <span
                      className="w-3 h-3 rounded-full inline-block shrink-0 border border-black/10"
                      style={{ backgroundColor: it.color }}
                      aria-hidden="true"
                    />
                  )}
                  <div className="flex flex-col min-w-0 flex-1">
                    <span className="truncate">
                      {it.label}
                      {it.badge && (
                        <span data-testid={`${triggerTestId}-badge-${it.id}`} className="ml-1.5 text-[9px] px-1 py-0 rounded bg-gray-200 text-gray-600 border border-gray-300 font-mono align-middle">{it.badge}</span>
                      )}
                    </span>
                    {it.sublabel && (
                      <span className="text-[10px] text-gray-500 font-mono truncate">{it.sublabel}</span>
                    )}
                  </div>
                </label>
              );
            })
          )}
        </div>
        {selectedArr.length > 0 && (
          <div className="p-2 border-t">
            <Button
              variant="ghost"
              size="sm"
              className="w-full text-xs h-7 text-gray-500"
              onClick={() => onChange([])}
              data-testid={`${triggerTestId}-clear`}
            >
              <X className="h-3 w-3 mr-1" /> Clear selection
            </Button>
          </div>
        )}
        {footer && (
          <div className="border-t px-2 py-1.5">{footer}</div>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default MultiSelectFilter;
